import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Download, Trash2, FileText, Upload, Archive } from "lucide-react";
import { toast } from "sonner";

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterTenant, setFilterTenant] = useState("all");
  const [form, setForm] = useState({ tenant_id: "", start_date: "", end_date: "", notes: "", file: null });

  const load = useCallback(async () => {
    try {
      const params = filterTenant !== "all" ? { tenant_id: filterTenant } : {};
      const [c, t] = await Promise.all([
        axios.get(`${API}/contracts`, { params }),
        axios.get(`${API}/tenants`),
      ]);
      setContracts(c.data);
      setTenants(t.data);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Error cargando contratos");
    } finally { setLoading(false); }
  }, [filterTenant]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ tenant_id: "", start_date: "", end_date: "", notes: "", file: null });
    setDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!form.file || !form.tenant_id || !form.start_date) {
      toast.error("Selecciona inquilino, fecha de inicio y archivo");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", form.file);
      formData.append("tenant_id", form.tenant_id);
      formData.append("start_date", form.start_date);
      formData.append("end_date", form.end_date || "");
      formData.append("notes", form.notes || "");
      await axios.post(`${API}/contracts`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Contrato subido correctamente");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo contrato");
    } finally { setUploading(false); }
  };

  const handleDownload = async (contract) => {
    try {
      const response = await axios.get(`${API}/contracts/${contract.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", contract.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando contrato"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este contrato?")) return;
    try {
      await axios.delete(`${API}/contracts/${id}`);
      toast.success("Contrato eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadAll = async () => {
    try {
      const response = await axios.get(`${API}/contracts/download-all`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Contratos_Backup.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup descargado");
    } catch { toast.error("Error descargando backup"); }
  };

  const getFileIcon = (contentType) => {
    if (contentType === "application/pdf") return "PDF";
    return "DOC";
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="contracts-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Contratos
          </h2>
          <p className="text-sm text-gray-500 mt-1">{contracts.length} contratos de arrendamiento</p>
        </div>
        <div className="flex items-center gap-2">
          {contracts.length > 0 && (
            <button
              onClick={handleDownloadAll}
              className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 flex items-center gap-2"
              data-testid="download-all-contracts-button"
            >
              <Archive className="w-4 h-4" /> Descargar todo (ZIP)
            </button>
          )}
          <button
            onClick={openNew}
            className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 flex items-center gap-2"
            data-testid="add-contract-button"
          >
            <Upload className="w-4 h-4" /> Subir contrato
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4 mb-4">
        <Select value={filterTenant} onValueChange={(v) => { setFilterTenant(v); setLoading(true); }}>
          <SelectTrigger className="w-56 rounded-sm" data-testid="filter-tenant-select">
            <SelectValue placeholder="Filtrar por inquilino" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los inquilinos</SelectItem>
            {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm" data-testid="contracts-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Tipo</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Archivo</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Inquilino</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Plaza</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Fecha inicio</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Fecha fin</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Tamaño</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-gray-400">No hay contratos subidos</TableCell>
              </TableRow>
            ) : contracts.map((c) => (
              <TableRow key={c.id} className="hover:bg-gray-50 transition-colors">
                <TableCell className="py-3 px-4">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold ${
                    c.content_type === "application/pdf" ? "bg-red-50 text-red-700 border border-red-200" : "bg-blue-50 text-blue-700 border border-blue-200"
                  }`}>
                    {getFileIcon(c.content_type)}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-gray-900 py-3 px-4 max-w-[200px] truncate">{c.original_filename}</TableCell>
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4">{c.tenant_name}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{c.parking_spot_identifier}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{c.start_date}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{c.end_date || "—"}</TableCell>
                <TableCell className="text-sm text-gray-500 py-3 px-4">{formatFileSize(c.size)}</TableCell>
                <TableCell className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => handleDownload(c)} className="p-1.5 text-gray-400 hover:text-[#002FA7] transition-colors" data-testid={`download-contract-${c.id}`}>
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-contract-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm" data-testid="contract-dialog">
          <DialogHeader>
            <DialogTitle>Subir contrato de arrendamiento</DialogTitle>
            <DialogDescription>Selecciona el archivo PDF o Word del contrato</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Inquilino</Label>
              <Select value={form.tenant_id} onValueChange={v => setForm({...form, tenant_id: v})}>
                <SelectTrigger className="rounded-sm" data-testid="contract-tenant-select">
                  <SelectValue placeholder="Selecciona inquilino" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Fecha de inicio</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="rounded-sm" data-testid="contract-start-date" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Fecha de fin (opcional)</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="rounded-sm" data-testid="contract-end-date" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Notas (opcional)</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notas adicionales..." className="rounded-sm" data-testid="contract-notes-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Archivo (PDF o Word)</Label>
              <div className="border border-gray-200 rounded-sm p-4">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={e => setForm({...form, file: e.target.files[0] || null})}
                  className="text-sm text-gray-600"
                  data-testid="contract-file-input"
                />
                {form.file && (
                  <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> {form.file.name} ({formatFileSize(form.file.size)})
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="contract-cancel-button">
              Cancelar
            </button>
            <button onClick={handleUpload} disabled={uploading} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 disabled:opacity-50" data-testid="contract-upload-button">
              <Upload className="w-4 h-4" /> {uploading ? "Subiendo..." : "Subir contrato"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
